import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

export default function RequirementForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { wage_type: 'daily', gender_preference: 'any', urgency: 'medium', number_of_workers: 1 }
  });

  useEffect(() => {
    Promise.all([api.get('/lookup/work-categories'), api.get('/lookup/skills')]).then(([c, s]) => {
      setCategories(c.data.data);
      setSkills(s.data.data);
    });
  }, []);

  async function onSubmit(data) {
    setLoading(true);
    try {
      const payload = {
        ...data,
        skills: selectedSkills.map((id) => ({ skill_id: id, is_mandatory: true })),
        number_of_workers: parseInt(data.number_of_workers, 10),
        accommodation: data.accommodation === 'true' || data.accommodation === true,
        food_provided:  data.food_provided  === 'true' || data.food_provided  === true,
        travel_provided: data.travel_provided === 'true' || data.travel_provided === true,
      };
      const res = await api.post('/requirements', payload);
      toast.success('Requirement created as draft');
      if (data.submit_for_approval) {
        await api.post(`/requirements/${res.data.data.requirementId}/submit`);
        toast.success('Submitted for admin approval');
      }
      navigate('/employer/requirements');
    } finally { setLoading(false); }
  }

  return (
    <EmployerLayout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Post a New Requirement</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white rounded-2xl border border-gray-200 p-6">

          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 border-b pb-2">Basic Details</h2>
            <Input label="Requirement Title" name="title" placeholder="e.g., Skilled Masons needed for Construction"
              required error={errors.title?.message}
              {...register('title', { required: 'Title is required' })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Work Category</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
                  {...register('work_category_id')}>
                  <option value="">Select category</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <Input label="Number of Workers" name="number_of_workers" type="number" min="1" required
                {...register('number_of_workers', { required: true, min: 1 })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Skills</label>
              <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {skills.map((s) => (
                  <button key={s.id} type="button"
                    onClick={() => setSelectedSkills((p) => p.includes(s.id) ? p.filter(x => x !== s.id) : [...p, s.id])}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      selectedSkills.includes(s.id) ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-24 focus:ring-2 focus:ring-brand-500 focus:outline-none"
                placeholder="Describe the work, location, specific requirements..." {...register('description')} />
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 border-b pb-2">Location & Dates</h2>
            <Input label="Work Location" name="location_text" placeholder="Village / City / District"
              {...register('location_text')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Start Date" name="start_date" type="date" {...register('start_date')} />
              <Input label="End Date" name="end_date" type="date" {...register('end_date')} />
            </div>
            <Input label="Application Deadline" name="application_deadline" type="date" {...register('application_deadline')} />
          </div>

          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 border-b pb-2">Compensation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input label="Wage From (₹)" name="wage_min" type="number" inputMode="numeric" {...register('wage_min')} />
              <Input label="Wage To (₹)" name="wage_max" type="number" inputMode="numeric" {...register('wage_max')} />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Wage Type</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
                  {...register('wage_type')}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="fixed">Fixed / Project</option>
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {[['accommodation', '🏠 Accommodation Provided'], ['food_provided', '🍽️ Food Provided'], ['travel_provided', '🚌 Travel Provided']].map(([name, label]) => (
                <label key={name} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded" {...register(name)} />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 border-b pb-2">Preferences</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Urgency</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
                  {...register('urgency')}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <Input label="Shift Hours/Day" name="shift_hours" type="number" min="1" max="24"
                {...register('shift_hours')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Contact Person" name="contact_person" {...register('contact_person')} />
              <Input label="Contact Mobile" name="contact_mobile" type="tel" {...register('contact_mobile')} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button variant="secondary" onClick={() => navigate('/employer/requirements')}>Cancel</Button>
            <Button variant="secondary" type="submit" loading={loading}>Save as Draft</Button>
            <Button type="button" loading={loading} onClick={handleSubmit((data) => onSubmit({ ...data, submit_for_approval: true }))}>
              Submit for Approval
            </Button>
          </div>
        </form>
      </div>
    </EmployerLayout>
  );
}
